import * as plugins from './smarttime.plugins.js';
import { CronJob, type TJobFunction } from './smarttime.classes.cronjob.js';
import { CronJobCollection } from './smarttime.classes.cronjobcollection.js';

export class CronManager {
  public executionTimeout: plugins.smartpromise.Timeout<void> | null = null;

  public status: 'started' | 'stopped' = 'stopped';
  public cronjobs = new CronJobCollection();

  private cycleWakeDeferred: plugins.smartpromise.Deferred<void> | null = null;

  /**
   * Incremented by every start(). A cycle loop only runs while it belongs to the
   * current generation, so a loop left over from before a stop()/start() pair exits
   * instead of running beside the new one.
   */
  private cycleGeneration = 0;

  constructor() {}

  /**
   * Resolves the current wake deferred, causing the sleeping cycle
   * to immediately recalculate instead of waiting for its timeout.
   */
  private wakeCycle() {
    if (this.cycleWakeDeferred && this.cycleWakeDeferred.status === 'pending') {
      this.cycleWakeDeferred.resolve();
    }
  }

  public addCronjob(cronIdentifierArg: string, cronFunctionArg: TJobFunction) {
    const newCronJob = new CronJob(this, cronIdentifierArg, cronFunctionArg);
    this.cronjobs.add(newCronJob);
    if (this.status === 'started') {
      newCronJob.start();
      this.wakeCycle();
    }

    return newCronJob;
  }

  public removeCronjob(cronjobArg: CronJob) {
    cronjobArg.stop();
    this.cronjobs.remove(cronjobArg);
    if (this.status === 'started') {
      this.wakeCycle();
    }
  }

  /**
   * starts the cronjob
   */
  public start() {
    if (this.status !== 'started') {
      this.status = 'started';
      for (const cronJob of this.cronjobs.getArray()) {
        cronJob.start();
      }
      this.cycleGeneration++;
      this.runCronCycle(this.cycleGeneration);
    }
  }

  private isCurrentCycle(generationArg: number) {
    return this.status === 'started' && this.cycleGeneration === generationArg;
  }

  private async runCronCycle(generationArg: number) {
    while (this.isCurrentCycle(generationArg)) {
      // Create a fresh wake signal for this iteration
      const wakeDeferred = new plugins.smartpromise.Deferred<void>();
      this.cycleWakeDeferred = wakeDeferred;

      // Check all cronjobs and find the soonest next execution
      let soonestMs = Infinity;
      for (const cronJob of this.cronjobs.getArray()) {
        cronJob.checkExecution();
        const msToNext = cronJob.getTimeToNextExecution();
        if (msToNext < soonestMs) {
          soonestMs = msToNext;
        }
      }

      // Sleep until the next job is due or until woken by a lifecycle event
      if (soonestMs < Infinity && soonestMs > 0) {
        const executionTimeout = new plugins.smartpromise.Timeout<void>(soonestMs);
        this.executionTimeout = executionTimeout;
        await Promise.race([executionTimeout.promise, wakeDeferred.promise]);
        // Cancel this iteration's own timeout to avoid lingering timers
        executionTimeout.cancel();
      } else if (soonestMs <= 0) {
        // Job is overdue, loop immediately to execute it
        continue;
      } else {
        // No jobs — wait indefinitely until woken by addCronjob or stop
        await wakeDeferred.promise;
      }
    }
    // A newer generation owns the wake signal once start() ran again
    if (this.cycleGeneration === generationArg) {
      this.cycleWakeDeferred = null;
    }
  }

  /**
   * stops all cronjobs
   */
  public stop() {
    if (this.status === 'started') {
      this.status = 'stopped';
      if (this.executionTimeout) {
        this.executionTimeout.cancel();
      }
      this.wakeCycle();
    }
    for (const cron of this.cronjobs.getArray()) {
      cron.stop();
    }
  }
}
